import { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SectionHeader } from '../common/SectionHeader';
import { Modal as QcsModal } from '@mui/material';

import { QcsModalBox } from '../common/basic/QcsModalBox';
import { identityActions, selectIdentity } from '../../store/entities/identity';
import { useAppDispatch, useAppSelector } from '../../store';
import { QcsFormControlLabel } from '../common/basic/QcsFormControlLabel';
import { QcsCheckbox } from '../common/basic/QcsCheckbox';
import { QcsButton } from '../common/basic/QcsButton';
import { firstUpper } from '../../utils/format';
import { hasRole } from '../../utils/roles';
import { HtmlViewer } from '../common/html/HtmlViewer';
import { UserRole } from '@api/models/enums/UserRole';
import { companyService, userService } from '@api/services/services';
import { resources } from '@api/resources';
import { ShowResourceContent } from '../ShowResourceContent';
import { useResourceLoading } from '@api/utils/ApiLoadingStore';
import { PageType } from '@api/models/enums/PageType';

const trackedResources = [resources.queryCompanyPages, resources.getUserPreferences, resources.updateUserPreferences];

export const WelcomeMessageModal: FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const identity = useAppSelector(selectIdentity);
  // The fetched page is tagged with the language it was fetched for, so a stale one can be
  // ignored during render instead of being cleared from the effect below.
  const [page, setPage] = useState<{ lang?: string; content: string }>({ content: '' });
  const [dontShow, setDontShow] = useState(false);
  const dispatch = useAppDispatch();
  const isLoading = useResourceLoading(trackedResources);

  const message = page.lang === i18n.resolvedLanguage ? page.content : '';
  // Inside a run the app locale follows the selected training language (TrainingRunLanguageSwitcher),
  // which is transient — it must not count as the user picking a new app language, or every run
  // language switch would mark that language's welcome page as seen.
  const isTrainingRun = location.pathname.startsWith('/training-run');
  const allowed = !isTrainingRun && identity.welcomeLangPages[i18n.resolvedLanguage!];
  const isExternalManager = hasRole(identity.roles, [UserRole.ExternalManager]);
  const isExternalWorker = hasRole(identity.roles, [UserRole.ExternalWorker]);
  const isExternalOneTime = hasRole(identity.roles, [UserRole.ExternalOneTime]);

  const getPageType = () => {
    if (isExternalManager) {
      return PageType.SupplierManager;
    } else if (isExternalWorker) {
      return PageType.SupplierEmployee;
    }

    return PageType.Visit;
  };

  const saveDontShow = async () => {
    const sett = 'settings' + firstUpper(i18n.resolvedLanguage!);

    const preferences = await userService.getPreferences(identity.id);
    const prefSett = (preferences.data as any)[sett] ?? {};

    const data = {
      ...preferences.data,
      [sett]: {
        ...prefSett,
        supplierEmployeeSeen: isExternalManager ? true : prefSett.supplierEmployeeSeen,
        supplierEmployeeWorkerSeen: isExternalWorker ? true : prefSett.supplierEmployeeWorkerSeen,
        visitationSeen: isExternalOneTime ? true : prefSett.visitationSeen,
      },
    };
    await userService.updatePreferences(identity.id, data, {}, { silentSuccess: true });
  };

  const saveStore = () => {
    dispatch(
      identityActions.success({
        ...identity,
        welcomeLangPages: {
          ...identity.welcomeLangPages,
          [i18n.resolvedLanguage!]: false,
        },
      }),
    );
  };

  useEffect(() => {
    (async () => {
      if (allowed) {
        const lang = i18n.resolvedLanguage;
        const pages = await companyService.queryCompanyPages({ type: getPageType(), language: lang });

        if (pages.data[0]?.content) {
          setPage({ lang, content: pages.data[0].content });
        } else {
          //No message, set dont show.
          await saveDontShow();
          saveStore();
        }
      }

      setDontShow(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, i18n.resolvedLanguage, identity.companyId]);

  const handleClose = async () => {
    if (isLoading) {
      return;
    }

    if (dontShow) {
      await saveDontShow();
    }

    saveStore();
  };

  const handleChange = () => {
    setDontShow(!dontShow);
  };

  // Only open once there is content for the current language: languages with no welcome page
  // are dismissed silently in the effect above.
  return (
    <QcsModal open={allowed && !!message} onClose={handleClose} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
      <QcsModalBox>
        <SectionHeader title="welcomeMessage.title" />
        <ShowResourceContent resource={trackedResources}>
          <HtmlViewer html={message} />
          <div>
            <QcsFormControlLabel control={<QcsCheckbox checked={dontShow} onChange={handleChange} />} label={t('welcomeMessage.check')} />
            <div>
              <QcsButton onClick={handleClose}>{t('common.close')}</QcsButton>
            </div>
          </div>
        </ShowResourceContent>
      </QcsModalBox>
    </QcsModal>
  );
};
